import React, {
  useState,
  useEffect,
  useRef,
  Fragment,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";
import PaginationTextView from "../../components/common/PaginationTextView";
import Pagination from "react-pagination-js";
import swal from "sweetalert";
import { Eye, MoreVertical } from "lucide-react";

const formatCurrency = (val) => {
  const num = Number(val ?? 0);
  const truncated = Math.trunc(num * 100) / 100;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated);
};

const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatBillAddr = (addr) => {
  if (!addr) return "—";
  return (
    [addr.Line1, addr.Line2, addr.Line3, addr.Line4, addr.City, addr.Country]
      .filter(Boolean)
      .join(", ") || "—"
  );
};

const formatShipAddr = (addr) => {
  if (!addr) return "—";
  return (
    [
      addr.Line1,
      addr.Line2,
      addr.Line3,
      addr.Line4,
      addr.City,
      addr.CountrySubDivisionCode,
      addr.PostalCode,
      addr.Country,
    ]
      .filter(Boolean)
      .join(", ") || "—"
  );
};
const normalize = (est) => {
  if (!est) return {};
  const raw = est.raw || {};
  const isQB = est.type === "quickbookEstimate";

  // ── Customer name ──
  const customerName =
    est.customerName ||
    est.customer_name ||
    raw.CustomerRef?.name ||
    est.displayName ||
    "—";

  // ── Email ──
  const email = est.email || raw.BillEmail?.Address || est.displayEmail || "—";

  // ── Doc number ──
  let docNumber =
    est.docNumber || est.doc_number || est.estimate_no || raw.DocNumber || "—";
  const customFields = raw.CustomField || est.CustomField || [];
  // let displayDocNumber = docNumber;
  const topsField = customFields.find(
    (f) => f.Name === "Tops Shipping Systems, Corp",
  );
  if (
    topsField &&
    topsField.StringValue &&
    topsField.StringValue !== "ORD - null"
  ) {
    docNumber = topsField.StringValue.replace("ORD - ", "");
  }
  // ── Status ──
  let rawStatus =
    est.status || raw.TxnStatus || est.mainRequest?.status || "PENDING";
  if (
    rawStatus === "ESTIMATE" ||
    rawStatus === "ESTIMATED" ||
    rawStatus === "estimated"
  ) {
    rawStatus = "Pending";
  } else if (rawStatus === "REJECT") {
    rawStatus = "REJECTED";
  } else if (rawStatus === "Converted") {
    rawStatus = "Closed";
  }
  const status = rawStatus;

  // ── Line items ──
  let rawLines = [];
  if (isQB) {
    rawLines =
      est.originalLineItems ||
      raw.Line?.filter((l) => l.DetailType === "SalesItemLineDetail") ||
      [];
  } else {
    rawLines = est.line_items || est.product_line_items || [];
  }

  const lines = rawLines.map((l) => ({
    id: l.Id || l._id || Math.random().toString(),
    name:
      l.Description ||
      l.description ||
      l.SalesItemLineDetail?.ItemRef?.name ||
      l.item_name ||
      "—",
    qty: Number(
      l.quantity || l.SalesItemLineDetail?.Qty || l.Qty || l.qty || 0,
    ),
    unitPrice: Number(
      l.rate ||
      l.unit_price ||
      l.SalesItemLineDetail?.UnitPrice ||
      l.UnitPrice ||
      l.unitPrice ||
      0,
    ),
    amount: Number(l.Amount || l.amount || 0),
  }));

  // ── Calculations ──
  const subTotal = lines.reduce((acc, line) => acc + line.amount, 0);

  let discountValue = 0;
  let isDiscountPercent = false;
  let discountAmount = 0;

  if (isQB) {
    const discountLine = raw.Line?.find(
      (l) => l.DetailType === "DiscountLineDetail",
    );
    if (discountLine) {
      discountAmount = Number(discountLine.Amount || 0);
      isDiscountPercent =
        discountLine.DiscountLineDetail?.PercentBased || false;
      discountValue = Number(
        discountLine.DiscountLineDetail?.DiscountPercent || 0,
      );
    }
  } else {
    discountValue = Number(est.discount_value || 0);
    isDiscountPercent = est.discount_type === "percent";
    discountAmount = isDiscountPercent
      ? ((subTotal + est?.markup_amount) * discountValue) / 100
      : discountValue;
  }

  const taxLines =
    isQB && raw.TxnTaxDetail?.TaxLine
      ? raw.TxnTaxDetail.TaxLine.map((tl) => ({
        amount: Number(tl.Amount || 0),
        percent: Number(tl.TaxLineDetail?.TaxPercent || 0),
        netAmount: Number(tl.TaxLineDetail?.NetAmountTaxable || 0),
      }))
      : [];

  const totalTax = Number(
    raw.TxnTaxDetail?.TotalTax ?? est.totalTax ?? est.total_tax ?? 0,
  );
  const total = subTotal - discountAmount;
  const grandTotal = total + totalTax;

  // ── Addresses ──
  let billAddr = null;
  if (isQB && raw.BillAddr) {
    billAddr = {
      Line1: raw.BillAddr.Line1,
      Line2: raw.BillAddr.Line2,
      Line3: raw.BillAddr.Line3,
      Line4: raw.BillAddr.Line4,
      City: raw.BillAddr.City,
      CountrySubDivisionCode: raw.BillAddr.CountrySubDivisionCode,
      PostalCode: raw.BillAddr.PostalCode,
      Country: raw.BillAddr.Country,
    };
  } else if (est.billingAddress) {
    billAddr = {
      Line1: est.billingAddress.bill_line1,
      Line2: est.billingAddress.bill_line2,
      City: est.billingAddress.bill_city,
      Country: est.billingAddress.bill_country,
      PostalCode: est.billingAddress.bill_postal_code,
    };
  }

  let shipAddr = null;
  if (isQB && raw.ShipAddr) {
    shipAddr = {
      Line1: raw.ShipAddr.Line1,
      Line2: raw.ShipAddr.Line2,
      Line3: raw.ShipAddr.Line3,
      Line4: raw.ShipAddr.Line4,
      City: raw.ShipAddr.City,
      CountrySubDivisionCode: raw.ShipAddr.CountrySubDivisionCode,
      PostalCode: raw.ShipAddr.PostalCode,
      Country: raw.ShipAddr.Country,
    };
  } else if (est.shippingAddress) {
    shipAddr = {
      Line1: est.shippingAddress.ship_line1,
      Line2: est.shippingAddress.ship_line2,
      City: est.shippingAddress.ship_city,
      Country: est.shippingAddress.ship_country,
      PostalCode: est.shippingAddress.ship_postal_code,
    };
  }

  return {
    _id: est._id,
    qbId: est.qbId || raw.Id || null,
    docNumber,
    customerName,
    email,
    status,
    txnDate: est.txnDate || est.txn_date || raw.TxnDate || null,
    expirationDate:
      est.expirationDate || est.expiration_date || raw.ExpirationDate || null,
    createdAt: est.createdAt || raw.MetaData?.CreateTime || null,
    updatedAt: est.updatedAt || raw.MetaData?.LastUpdatedTime || null,

    // Amounts
    new_amount: est?.totalAmount || est?.total_amount || 0,
    subTotal,
    discountAmount,
    discountValue,
    isDiscountPercent,
    totalTax,
    totalAmount: total, // net amount (subtotal - discount)
    grandTotal, // total + tax
    total_amount: est?.total_amount || grandTotal,
    markup_type: est.markup_type || est?.markup?.type || null,
    markup_amount: est.markup_amount || est?.markup?.amount || 0,
    markup_value: est.markup_value || est?.markup?.value || 0,
    // Additional Details
    BillAddr: billAddr,
    ShipAddr: shipAddr,
    Line: lines,
    taxLines,
    currencyCode: raw.CurrencyRef?.value || "USD",
    CustomerMemo: est.customer_memo || raw.CustomerMemo?.value || null,
    privateNote: est.private_note || raw.PrivateNote || null,
    negotiateMessage: est.negotiateMessage || raw.negotiateMessage || null,
    shipMethod: est.ship_method || raw.ShipMethodRef?.name || null,
    addedBy: est.addedBy || null,

    // Source flags
    _isQB: isQB || !!raw.Id,
    _isLocal: est.type === "generateQuote" || !!est._id,
    _type: est.type || "unknown",
  };
};

const STATUS_TABS = [
  { label: "All", value: "ALL" },
  { label: "Accepted", value: "ACCEPTED" },
  { label: "Pending", value: "PENDING" },
  { label: "Negotiate", value: "NEGOTIATE" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Closed", value: "CLOSED" },
];

const statusColor = (status = "") => {
  const map = {
    Accepted: {
      bg: "bg-[#F0FEF4]",
      text: "text-[#008236]",
      border: "border-[#0a7822a6]",
      dot: "bg-[#0a7822a6]",
    },
    Pending: {
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-200",
      dot: "bg-amber-500",
    },
    Closed: {
      bg: "bg-gray-100",
      text: "text-gray-500",
      border: "border-gray-200",
      dot: "bg-gray-400",
    },
    Rejected: {
      bg: "bg-red-50",
      text: "text-red-600",
      border: "border-red-200",
      dot: "bg-red-500",
    },
    negotiate: {
      bg: "bg-purple-50",
      text: "text-purple-600",
      border: "border-purple-200",
      dot: "bg-purple-500",
    },
  };
  return (
    map[status] || {
      bg: "bg-gray-100",
      text: "text-gray-500",
      border: "border-gray-200",
      dot: "bg-gray-400",
    }
  );
};

const StatusBadge = ({ status }) => {
  const c = statusColor(status || "");
  const label = status ? status : "—";
  return (
    <span
      className={`inline-flex capitalize items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium border ${c.bg} ${c.text} ${c.border}`}
    >
      {/* <span className={`size-1.5 rounded-full ${c.dot}`} /> */}
      {label}
    </span>
  );
};

const SourceBadge = ({ isQB, isLocal }) => {
  if (isQB && isLocal)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#115B9D]/8 border border-[#115B9D]/15 text-[10px] font-semibold text-[#115B9D] capitalize">
        QB + Local
      </span>
    );
  if (isQB)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#f0fdf4] border border-green-200 text-[10px] font-semibold text-[#008236] capitalize">
        QuickBooks
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-[10px] font-semibold text-gray-500 capitalize">
      Local
    </span>
  );
};

const computeTabCounts = (list) => {
  const counts = { ALL: list.length };
  STATUS_TABS.forEach((tab) => {
    if (tab.value === "ALL") return;
    counts[tab.value] = list.filter((e) => e.status === tab.value).length;
  });
  return counts;
};

const StatCard = ({ label, value, bg, iconClass, svgPath }) => (
  <div className="border border-gray-200 rounded-2xl bg-white px-5 py-4 flex items-center gap-4">
    <div
      className={`size-12 rounded-xl ${bg} flex items-center justify-center shrink-0`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={iconClass}
      >
        {svgPath}
      </svg>
    </div>
    <div className="max-sm:w-full">
      <p className="text-sm text-gray-600 font-medium capitalize">{label}</p>
      <p className="text-md sm:text-xl font-semibold text-gray-800 mt-0.5">
        {value}
      </p>
    </div>
  </div>
);

export default function QuickBookEstimateList({ subTab, metaId }) {
  const user = useSelector((state) => state.user);
  const history = useNavigate();
  const isConnected = !!user?.quick_book_accesToken;

  const [estimates, setEstimates] = useState([]);
  const [total, setTotal] = useState(0);
  const [openMenu, setOpenMenu] = useState(null);
  const [tabCounts, setTabCounts] = useState({});
  const [summaryStats, setSummaryStats] = useState({
    totalCount: 0,
    totalAmt: 0,
    totalTax: 0,
  });

  const [filters, setFilters] = useState({
    page: 1,
    count: 50,
    status: subTab || "ALL",
    search: "",
  });
  const [searchInput, setSearchInput] = useState("");
  const [pageLoading, setPageLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const searchDebounceRef = useRef(null);
  const pollingRef = useRef(null);
  const prevTabCountsRef = useRef({});

  const buildParams = (f) => {
    const p = new URLSearchParams();
    p.set("page", f.page);
    p.set("limit", f.count);
    if (f.status && f.status !== "ALL") p.set("status", f.status);
    if (f.search) p.set("search", encodeURIComponent(f.search));
    return p.toString();
  };

  const showBrowserNotification = useCallback((title, body) => {
    if (!("Notification" in window)) return;
    const send = () => new Notification(title, { body, icon: "/favicon.ico" });
    if (Notification.permission === "granted") {
      send();
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then((p) => {
        if (p === "granted") send();
      });
    }
  }, []);

  const notifyIfNewItems = useCallback(
    (newCounts) => {
      const prev = prevTabCountsRef.current;

      const prevNeg = prev?.NEGOTIATE ?? 0;
      const newNeg = newCounts?.NEGOTIATE ?? 0;
      if (newNeg > prevNeg) {
        showBrowserNotification(
          `${newNeg - prevNeg} New Negotiation Request${newNeg - prevNeg > 1 ? "s" : ""}`,
          "Click to review the estimate(s) pending negotiation.",
        );
      }

      const prevPend = prev?.PENDING ?? 0;
      const newPend = newCounts?.PENDING ?? 0;
      if (newPend > prevPend) {
        showBrowserNotification(
          `${newPend - prevPend} New Pending Estimate${newPend - prevPend > 1 ? "s" : ""}`,
          "New pending estimates are waiting for review.",
        );
      }
    },
    [showBrowserNotification],
  );

  const fetchEstimates = useCallback(
    async (activeFilters, silent = false) => {
      if (!silent) {
        setPageLoading(true);
        loader(true);
      }
      try {
        const res = await ApiClient.get(
          `quickbooks-docs/estimates/list?${buildParams(activeFilters)}`,
        ).catch(() => null);

        if (res?.success) {
          const rawList = Array.isArray(res.data) ? res.data : [];
          const normalized = rawList.map(normalize);
          const paginationTotal =
            res.pagination?.total ?? res.total ?? normalized.length;

          setEstimates(normalized);
          setTotal(paginationTotal);

          if (res.statusCounts && typeof res.statusCounts === "object") {
            const sc = {};
            Object.entries(res.statusCounts).forEach(([k, v]) => {
              sc[k] = v;
            });
            if (!sc.ALL) sc.ALL = paginationTotal;
            notifyIfNewItems(sc);
            prevTabCountsRef.current = sc;
          } else {
            const computed = computeTabCounts(normalized);
            computed.ALL = paginationTotal;
          }

          setSummaryStats({
            totalCount: paginationTotal,
            totalAmt: normalized.reduce((s, e) => s + e.grandTotal, 0),
            totalTax: normalized.reduce((s, e) => s + e.totalTax, 0),
          });
        } else {
          setEstimates([]);
          setTotal(0);
        }
      } catch (err) {
        console.error("fetchEstimates error:", err);
      } finally {
        if (!silent) {
          setPageLoading(false);
          loader(false);
        }
      }
    },
    [notifyIfNewItems],
  );

  const fetchAllCounts = useCallback(async () => {
    if (!isConnected) return;
    try {
      const [ALL, PENDING, NEGOTIATE, REJECTED, ACCEPTED, CLOSED] =
        await Promise.allSettled([
          ApiClient.get("quickbooks-docs/estimates/list", {
            page: 1,
            limit: 1,
            status: "",
          }),
          ApiClient.get("quickbooks-docs/estimates/list", {
            page: 1,
            limit: 1,
            status: "Pending",
          }),

          ApiClient.get("quickbooks-docs/estimates/list", {
            page: 1,
            limit: 1,
            status: "negotiate",
          }),
          ApiClient.get("quickbooks-docs/estimates/list", {
            page: 1,
            limit: 1,
            status: "rejected",
          }),
          ApiClient.get("quickbooks-docs/estimates/list", {
            page: 1,
            limit: 1,
            status: "Accepted",
          }),
          ApiClient.get("quickbooks-docs/estimates/list", {
            page: 1,
            limit: 1,
            status: "CLOSED",
          }),
        ]);

      setTabCounts({
        PENDING:
          PENDING.status === "fulfilled" && PENDING.value?.success
            ? PENDING.value?.pagination?.total || 0
            : 0,
        ACCEPTED:
          ACCEPTED.status === "fulfilled" && ACCEPTED.value?.success
            ? ACCEPTED.value?.pagination?.total || 0
            : 0,
        ALL:
          ALL.status === "fulfilled" && ALL.value?.success
            ? ALL.value?.pagination?.total || 0
            : 0,
        NEGOTIATE:
          NEGOTIATE.status === "fulfilled" && NEGOTIATE.value?.success
            ? NEGOTIATE.value?.pagination?.total || 0
            : 0,
        REJECTED:
          REJECTED.status === "fulfilled" && REJECTED.value?.success
            ? REJECTED.value?.pagination?.total || 0
            : 0,
        CLOSED:
          CLOSED.status === "fulfilled" && CLOSED.value?.success
            ? CLOSED.value?.pagination?.total || 0
            : 0,
      });
    } catch (error) {
      console.error("Error fetching counts:", error);
    }
  }, [isConnected, user]);

  useEffect(() => {
    fetchEstimates(filters);
    fetchAllCounts();
  }, [filters, fetchEstimates]);

  useEffect(() => {
    if (!isConnected) return;
    clearInterval(pollingRef.current);
    pollingRef.current = setInterval(() => {
      fetchEstimates(filters, true);
      fetchAllCounts();
    }, 30000);
    return () => clearInterval(pollingRef.current);
  }, [isConnected, filters, fetchEstimates]);

  useEffect(() => {
    if (!subTab) return;
    const tabMap = {
      pending: "PENDING",
      reject: "REJECTED",
      rejected: "REJECTED",
      negotiate: "NEGOTIATE",
      negotiation: "NEGOTIATE",
      accepted: "ACCEPTED",
      synced: "ACCEPTED",
      invoice: "ACCEPTED",
      closed: "CLOSED",
    };
    const mapped = tabMap[subTab.toLowerCase()];
    if (mapped) setFilters((prev) => ({ ...prev, status: mapped, page: 1 }));
  }, [subTab]);

  const handleTabChange = (value) =>
    setFilters((prev) => ({ ...prev, status: value, page: 1 }));

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: value, page: 1 }));
    }, 400);
  };

  const handlePageChange = (page) => setFilters((prev) => ({ ...prev, page }));

  const canConvert = (est) => est?.status === "Accepted" && est?._isQB;

  const handleAcceptReject = (item, statusStr) => {
    const isAccepting = statusStr === "estimated" || statusStr === "accepted";
    const statusApiStr = statusStr === "estimated" ? "estimated" : "reject";

    swal({
      title: isAccepting ? "Accept Quote" : "Reject Quote",
      text: `Are you sure you want to ${isAccepting ? "accept" : "reject"} this quote?`,
      icon: "warning",
      buttons: true,
      dangerMode: !isAccepting,
    }).then((willConfirm) => {
      if (willConfirm) {
        loader(true);
        ApiClient.put("generate-quote/update", {
          status: statusApiStr,
          id: item?._id,
        }).then((res) => {
          if (res.success) {
            fetchEstimates(filters);
            fetchAllCounts();
            swal({
              icon: "success",
              title: isAccepting ? "Estimate Accepted!" : "Estimate Rejected!",
              text: isAccepting
                ? "The estimate has been accepted successfully and moved to the Pending tab. Click below to view the details."
                : "The estimate has been rejected successfully and moved to the Rejected tab. Click below to view the details.",
              buttons: {
                cancel: {
                  text: "Close",
                  value: false,
                  visible: true,
                },
                confirm: {
                  text: "View Details",
                  value: true,
                },
              },
            }).then((viewDetails) => {
              if (viewDetails) {
                history(`/estimate/detail/${res?.data?._id}`);
              }
            });
          } else {
            swal(
              "Error",
              res.error?.message || "Something went wrong.",
              "error",
            );
          }
          loader(false);
        });
      }
    });
  };

  if (!isConnected) {
    return (
      <Layout>
        <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
          <div className="flex flex-col gap-1 mb-6">
            <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold uppercase">
              Estimates
            </h2>
            <p className="text-sm sm:text-md text-gray-600">
              Manage your QuickBooks estimates
            </p>
          </div>
          <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
            <div className="flex flex-col items-center justify-center py-20 gap-6 text-center">
              <div className="size-20 rounded-2xl bg-[#2CA01C]/10 border border-[#2CA01C]/20 flex items-center justify-center">
                <svg viewBox="0 0 40 40" width="40" height="40">
                  <circle
                    cx="18"
                    cy="18"
                    r="10"
                    fill="none"
                    stroke="#2CA01C"
                    strokeWidth="3"
                  />
                  <line
                    x1="25"
                    y1="25"
                    x2="31"
                    y2="31"
                    stroke="#2CA01C"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="space-y-1.5">
                <h3 className="text-lg font-semibold text-gray-900 uppercase">
                  QuickBooks Not Connected
                </h3>
                <p className="text-sm text-gray-500 max-w-sm uppercase">
                  Connect your QuickBooks account to view and manage your
                  estimates.
                </p>
              </div>
              <button
                type="button"
                onClick={() => history("/quickbook/integration")}
                className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl font-medium px-5 py-2.5 uppercase"
              >
                Connect QuickBooks
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
        {/* ─── Summary Cards ─── */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <StatCard
            label="Total Estimates"
            value={summaryStats.totalCount}
            bg="bg-[#115B9D17]"
            iconClass="text-[#115B9D]"
            svgPath={
              <>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <line x1="10" y1="9" x2="8" y2="9" />
              </>
            }
          />

          <StatCard
            label="Total Amount"
            value={formatCurrency(summaryStats.totalAmt)}
            bg="bg-[#f0fdf4]"
            iconClass="text-[#008236]"
            svgPath={
              <>
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </>
            }
          />

          {/* Full width on md, normal on lg */}
          <div className="col-span-2 lg:col-span-1">
            <StatCard
              label="Total Tax"
              value={formatCurrency(summaryStats.totalTax)}
              bg="bg-amber-50"
              iconClass="text-amber-600"
              svgPath={
                <>
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </>
              }
            />
          </div>
        </div>

        <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
          {/* ─── Filters Row ─── */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="max-sm:w-full overflow-auto flex items-center gap-1 bg-gray-100 rounded-xl max-lg:[scrollbar-width:none] max-lg:[-ms-overflow-style:none] max-lg:[&::-webkit-scrollbar]:hidden p-1.5">
              {STATUS_TABS.map((tab) => {
                const isActive = filters.status === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => handleTabChange(tab.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1.5 capitalize ${isActive
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                      }`}
                  >
                    {tab.label}
                    <span
                      className={`text-[11px] font-semibold px-[4px] min-w-[24px] h-[24px] flex items-center justify-center rounded-full ${isActive
                        ? "bg-[#D8E4EF] text-[#115B9D]"
                        : "bg-[#D8E4EF] text-[#7C7C7C]"
                        }`}
                    >
                      {tabCounts[tab.value]}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="relative w-full sm:w-[350px]">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                placeholder="Search By Customer, Doc..."
                value={searchInput}
                onChange={handleSearchChange}
                className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all capitalize"
              />
            </div>
          </div>

          {/* ─── Table ─── */}
          {/* Mobile View */}
          <div className="md:hidden space-y-4">
            {pageLoading ? (
              <div className="bg-white border rounded-xl p-6 text-center">
                Loading estimates...
              </div>
            ) : estimates.length === 0 ? (
              <div className="bg-white border rounded-xl p-6 text-center">
                No estimates found
              </div>
            ) : (
              estimates.map((est, index) => (
                <div
                  key={est._id || est.qbId || index}
                  className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="w-[40px] min-w-[40px] h-[40px] rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center">
                        <span className="text-xs font-semibold text-[#115B9D]">
                          {getInitials(est.customerName)}
                        </span>
                      </div>

                      <div>
                        <h2 className="text-sm font-semibold text-gray-900 capitalize">
                          {est.customerName}
                        </h2>

                        <p className="text-xs text-gray-500">
                          #{est.docNumber}
                        </p>
                      </div>
                    </div>

                    <StatusBadge status={est.status} />
                  </div>

                  {/* Details */}
                  <div className="mt-4 divide-y divide-gray-100">
                    <div className="flex justify-between items-center py-2">
                      <div className="">
                        <p className="text-xs text-gray-500">Email</p>
                        <p className="text-sm text-gray-700 break-all">
                          {est.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-gray-500">Date</span>
                      <span className="text-sm text-gray-800">
                        {formatDate(est.txnDate)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-gray-500">Source</span>
                      <SourceBadge isQB={est._isQB} isLocal={est._isLocal} />
                    </div>

                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-gray-500">Tax</span>
                      <span className="text-sm text-gray-700">
                        {formatCurrency(est.totalTax, est.currencyCode)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-gray-500">
                        Total Amount
                      </span>
                      <span className="font-semibold text-[#115B9D]">
                        {formatCurrency(est.new_amount, est.currencyCode)}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-4 flex flex-wrap gap-2 border-t pt-3">
                    <button
                      onClick={() =>
                        history(`/estimate/detail/${est?._id || est?.qbId}`)
                      }
                      className="flex-1 min-w-[90px] h-10 rounded-xl border border-gray-200 text-sm"
                    >
                      View
                    </button>

                    {est.status === "negotiate" && (
                      <>
                        <button
                          onClick={() => handleAcceptReject(est, "estimated")}
                          className="flex-1 min-w-[90px] h-10 rounded-xl bg-green-600 text-white text-sm"
                        >
                          Accept
                        </button>

                        <button
                          onClick={() => handleAcceptReject(est, "reject")}
                          className="flex-1 min-w-[90px] h-10 rounded-xl bg-red-600 text-white text-sm"
                        >
                          Reject
                        </button>

                        <button
                          onClick={() =>
                            history(`/edit/quote/${est?.qbId || est?._id}`)
                          }
                          className="flex-1 min-w-[90px] h-10 rounded-xl bg-[#115B9D] text-white text-sm"
                        >
                          Edit
                        </button>
                      </>
                    )}

                    {canConvert(est) && (
                      <button
                        onClick={() =>
                          history(`/generate/invoice/${est?.qbId || est?._id}`)
                        }
                        className="w-full h-10 rounded-xl bg-gray-900 text-white text-sm"
                      >
                        Convert To Invoice
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block w-full border border-gray-200 rounded-xl overflow-visible">
            <table className="w-full">
              <colgroup>
                <col style={{ width: "6%" }} />  {/* Actions */}
                <col style={{ width: "14%" }} /> {/* Doc # */}
                <col style={{ width: "14%" }} /> {/* Customer */}
                <col style={{ width: "9%" }} />  {/* Txn Date */}
                <col style={{ width: "9%" }} />  {/* Status */}
                <col style={{ width: "10%" }} /> {/* Source */}
                <col style={{ width: "7%" }} />  {/* Total Tax */}
                <col style={{ width: "22%" }} /> {/* Email */}
                <col style={{ width: "9%" }} />  {/* Total Amount */}
              </colgroup>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[
                    "Actions",
                    "Doc #",
                    "Customer",
                    "Txn Date",
                    "Status",
                    "Source",
                    "Total Tax",
                    "Email",
                    "Total Amount",
                  ].map((h) => (
                    <th key={h} className="text-left py-3 px-3">
                      <span className="text-xs text-gray-700 font-medium whitespace-nowrap capitalize">
                        {h}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageLoading ? (
                  <tr>
                    {/* <td colSpan={9} className="text-center py-16">
            <div className="flex flex-col items-center gap-3">
              <div className="size-8 border-2 border-[#115B9D] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-600 capitalize">
                Loading estimates...
              </p>
            </div>
          </td> */}
                  </tr>
                ) : estimates.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-600"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500 capitalize">
                          No estimates found
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  estimates.map((est, index) => (
                    <tr
                      key={est._id || est.qbId || index}
                      className={`transition-colors cursor-pointer ${openMenu === (est._id || est.qbId)
                        ? "relative z-[999]"
                        : "hover:bg-gray-50/60"
                        }`}
                    >
                      <td className="py-3 px-2">
                        <div className="relative flex justify-center overflow-visible">
                          <button
                            type="button"
                            onClick={() =>
                              setOpenMenu(
                                openMenu === (est._id || est.qbId)
                                  ? null
                                  : (est._id || est.qbId)
                              )
                            }
                            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600"
                          >
                            <MoreVertical size={16} />
                          </button>

                          {openMenu === (est._id || est.qbId) && (
                            <div className="absolute left-14 top-0 z-[9999] w-56 rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
                              <button
                                className="flex w-full items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-gray-700 transition-colors"
                                onClick={() => {
                                  history(`/estimate/detail/${est?._id || est?.qbId}`);
                                  setOpenMenu(null);
                                }}
                              >
                                <Eye size={16} className="text-[#115B9D]" />
                                <span className="text-sm font-medium">View Details</span>
                              </button>

                              {est.status === "negotiate" && (
                                <>
                                  <button
                                    className="flex w-full items-center gap-3 px-4 py-2.5 hover:bg-green-50 text-green-600"
                                    onClick={() => {
                                      handleAcceptReject(est, "estimated");
                                      setOpenMenu(null);
                                    }}
                                  >
                                    ✓ Accept
                                  </button>

                                  <button
                                    className="flex w-full items-center gap-3 px-4 py-2.5 hover:bg-red-50 text-red-600"
                                    onClick={() => {
                                      handleAcceptReject(est, "reject");
                                      setOpenMenu(null);
                                    }}
                                  >
                                    ✕ Reject
                                  </button>

                                  <button
                                    className="flex w-full items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-gray-700"
                                    onClick={() => {
                                      history(`/edit/quote/${est?.qbId || est?._id}`);
                                      setOpenMenu(null);
                                    }}
                                  >
                                    ✏ Edit
                                  </button>
                                </>
                              )}

                              {canConvert(est) && (
                                <button
                                  className="flex w-full items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-[#115B9D]"
                                  onClick={() => {
                                    history(`/generate/invoice/${est?.qbId || est?._id}`);
                                    setOpenMenu(null);
                                  }}
                                >
                                  💲 Convert To Invoice
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Doc # */}
                      <td className="py-3 px-2">
                        <span
                          className="inline-flex items-center px-2 py-1 rounded-lg bg-[#115B9D]/8 border border-[#115B9D]/15 text-[11px] font-semibold text-[#115B9D] truncate max-w-full"
                          title={est.docNumber}
                        >
                          {est.docNumber}
                        </span>
                      </td>

                      {/* Customer */}
                      <td className="py-3 px-2 capitalize overflow-hidden">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="size-7 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                            <span className="text-[9px] font-semibold text-[#115B9D]">
                              {getInitials(est.customerName)}
                            </span>
                          </div>
                          <p
                            className="text-xs font-medium text-gray-800 break-words leading-tight"
                            title={est.customerName}
                          >
                            {est.customerName}
                          </p>
                        </div>
                      </td>

                      {/* Txn Date */}
                      <td className="py-3 px-2">
                        <p className="text-xs text-gray-700 whitespace-nowrap">
                          {formatDate(est.txnDate)}
                        </p>
                      </td>

                      {/* Status */}
                      <td className="py-3 px-2">
                        <StatusBadge status={est.status} />
                      </td>

                      {/* Source */}
                      <td className="py-3 px-2">
                        <SourceBadge isQB={est._isQB} isLocal={est._isLocal} />
                      </td>

                      {/* Total Tax */}
                      <td className="py-3 px-2">
                        <p className="text-xs text-gray-500 truncate">
                          {formatCurrency(est.totalTax, est.currencyCode)}
                        </p>
                      </td>

                      {/* Email — full show, no truncate */}
                      <td className="py-3 px-2 overflow-hidden">
                        <p
                          className="text-xs text-gray-500 break-all leading-tight"
                          title={est.email}
                        >
                          {est.email}
                        </p>
                      </td>

                      {/* Total Amount — now last column */}
                      <td className="py-3 px-2">
                        <p className="text-xs font-semibold text-gray-800 truncate">
                          {formatCurrency(est.new_amount, est.currencyCode)}
                        </p>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > filters.count && (
            <div className="mt-5 flex flex-col gap-4 border-t border-gray-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-600">
                  {PaginationTextView(filters.page, filters.count, total)}
                </p>
              </div>
              <div className="custom-pagination">
                <Pagination
                  currentPage={filters.page}
                  totalSize={total}
                  sizePerPage={filters.count}
                  changeCurrentPage={handlePageChange}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
